// src/components/Nav.js
import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import translations from '../translations';

const LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'ja', label: '日本語' },
  { code: 'mn', label: 'Монгол' },
  { code: 'ur', label: 'اردو' },
  { code: 'ru', label: 'Русский' },
];

const LANG_CODES = LANGUAGES.map((l) => l.code);

function getPagePath(pathname) {
  // Strip language prefix if present
  for (const code of LANG_CODES) {
    if (code === 'en') continue;
    if (pathname === `/${code}` || pathname === `/${code}/`) return '/';
    if (pathname.startsWith(`/${code}/`)) return pathname.slice(code.length + 1);
  }
  return pathname || '/';
}

function buildLangPath(targetLang, pagePath) {
  const page = pagePath === '/' ? '' : pagePath;
  if (targetLang === 'en') return page || '/';
  return `/${targetLang}${page || '/'}`;
}

function Nav({ language = 'en' }) {
  const t = translations[language] || translations['en'];
  const nav = t.nav || {};
  const prefix = language === 'en' ? '' : `/${language}`;
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);

  const links = [
    { label: nav.home || 'Home', to: `${prefix}/` },
    { label: nav.whatIsErc || 'What is ERC?', to: `${prefix}/what-is-erc` },
    { label: nav.howToFind || 'How to Find Your Code', to: `${prefix}/how-to-find-erc-code` },
    { label: nav.supportedModels || 'Supported Models', to: `${prefix}/supported-models` },
    { label: nav.faq || 'FAQ', to: `${prefix}/faq` },
    { label: nav.troubleshooting || 'Troubleshooting', to: `${prefix}/troubleshooting` },
  ];

  const isActive = (to) => {
    if (to === `${prefix}/`) {
      return location.pathname === to || location.pathname === `${prefix}`;
    }
    return location.pathname === to;
  };

  const pagePath = getPagePath(location.pathname);

  return (
    <nav style={{
      position: 'sticky',
      top: 0,
      zIndex: 100,
      background: '#ffffff',
      borderBottom: '1px solid #e5e7eb',
      boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
    }}>
      {/* Main nav row */}
      <div style={{
        maxWidth: 960,
        margin: '0 auto',
        padding: '0 16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        height: 52,
      }}>
        <Link to={`${prefix}/`} style={{ fontWeight: 700, color: '#2563eb', textDecoration: 'none', fontSize: 15, whiteSpace: 'nowrap' }}>
          ERC Unlock
        </Link>

        {/* Desktop links */}
        <div style={{ display: 'flex', gap: 4, flexWrap: 'nowrap', alignItems: 'center' }} className="nav-desktop">
          {links.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              style={{
                padding: '6px 10px',
                borderRadius: 8,
                textDecoration: 'none',
                fontSize: 13,
                fontWeight: isActive(link.to) ? 700 : 500,
                color: isActive(link.to) ? '#2563eb' : '#374151',
                background: isActive(link.to) ? '#eff6ff' : 'transparent',
                transition: 'background 0.15s, color 0.15s',
                whiteSpace: 'nowrap',
              }}
            >
              {link.label}
            </Link>
          ))}
        </div>

        {/* Hamburger for mobile */}
        <button
          className="nav-hamburger"
          onClick={() => setMenuOpen(!menuOpen)}
          style={{
            display: 'none',
            background: 'none',
            border: '1px solid #e5e7eb',
            borderRadius: 8,
            padding: '6px 10px',
            cursor: 'pointer',
            fontSize: 18,
            color: '#374151',
          }}
          aria-label="Toggle menu"
        >
          {menuOpen ? '✕' : '☰'}
        </button>
      </div>

      {/* Mobile menu */}
      {menuOpen && (
        <div className="nav-mobile-menu" style={{
          background: '#fff',
          borderTop: '1px solid #e5e7eb',
          padding: '8px 16px 12px',
        }}>
          {links.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              onClick={() => setMenuOpen(false)}
              style={{
                display: 'block',
                padding: '10px 8px',
                borderRadius: 8,
                textDecoration: 'none',
                fontSize: 14,
                fontWeight: isActive(link.to) ? 700 : 500,
                color: isActive(link.to) ? '#2563eb' : '#374151',
                background: isActive(link.to) ? '#eff6ff' : 'transparent',
                marginBottom: 2,
              }}
            >
              {link.label}
            </Link>
          ))}
        </div>
      )}

      {/* Language switcher bar */}
      <div style={{
        borderTop: '1px solid #f0f0f0',
        background: '#f9fafb',
        display: 'flex',
        justifyContent: 'center',
        gap: 4,
        padding: '4px 16px',
      }}>
        {LANGUAGES.map((lang) => {
          const isCurrentLang = lang.code === language;
          const targetPath = buildLangPath(lang.code, pagePath);
          return (
            <Link
              key={lang.code}
              to={targetPath}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                padding: '3px 10px',
                borderRadius: 20,
                textDecoration: 'none',
                fontSize: 12,
                fontWeight: isCurrentLang ? 700 : 500,
                color: isCurrentLang ? '#1e3a8a' : '#6b7280',
                background: isCurrentLang ? '#dbeafe' : 'transparent',
                border: isCurrentLang ? '1px solid #bfdbfe' : '1px solid transparent',
                transition: 'background 0.15s, color 0.15s',
              }}
            >
              {lang.label}
            </Link>
          );
        })}
      </div>

      <style>{`
        @media (max-width: 700px) {
          .nav-desktop { display: none !important; }
          .nav-hamburger { display: block !important; }
        }
      `}</style>
    </nav>
  );
}

export default Nav;
